import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { adminApi } from "../../api/admin";

const fmtDay = (d) => {
  const [, m, day] = d.split("-");
  return `${m}/${day}`;
};

const tooltipStyle = { background: "#0a0a0a", border: "1px solid #FF5500" };
const axisProps = { stroke: "#A1A1AA", fontSize: 11 };

function ChartCard({ title, children }) {
  return (
    <div className="feature-card rounded-lg p-4 md:p-6">
      <p className="text-xs text-[#A1A1AA] mb-3 font-mono uppercase tracking-wider">{title}</p>
      <ResponsiveContainer width="100%" height={220}>{children}</ResponsiveContainer>
    </div>
  );
}

// Human-readable names for the buckets services/attribution.py emits. An
// unknown key falls back to itself rather than rendering blank, so a bucket
// added server-side shows up instead of silently disappearing.
const SOURCE_LABELS = {
  meta_ads: "Meta ads",
  google_ads: "Google ads",
  tiktok: "TikTok",
  other: "Other tagged",
  direct: "Direct / organic",
};

const ACQ_COLS = [
  ["leads", "Leads"],
  ["accounts", "Accounts"],
  ["trials", "Trials"],
  ["purchases", "Purchases"],
];

function AcquisitionTable({ data }) {
  const rows = data?.rows ?? [];
  if (!rows.length) return null;

  return (
    <div className="feature-card rounded-lg p-4 md:p-6 mb-4">
      <p className="text-xs text-[#A1A1AA] mb-3 font-mono uppercase tracking-wider">
        Acquisition by source
      </p>
      {/* Narrow screens scroll the table rather than squashing the columns. */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[#A1A1AA] text-xs font-mono uppercase tracking-wider">
              <th className="text-left py-2 pr-4 font-normal">Source</th>
              {ACQ_COLS.map(([key, label]) => (
                <th key={key} className="text-right py-2 px-3 font-normal">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.source} className="border-t border-[#1a1a1e]">
                <td className="text-left py-2 pr-4 text-white">
                  {SOURCE_LABELS[r.source] ?? r.source}
                </td>
                {ACQ_COLS.map(([key]) => (
                  <td key={key} className="text-right py-2 px-3 text-white font-mono">{r[key]}</td>
                ))}
              </tr>
            ))}
            {data?.totals && (
              <tr className="border-t border-[#2a2a2e]">
                <td className="text-left py-2 pr-4 text-[#A1A1AA] font-mono uppercase text-xs tracking-wider">
                  Total
                </td>
                {ACQ_COLS.map(([key]) => (
                  <td key={key} className="text-right py-2 px-3 text-[#A1A1AA] font-mono">
                    {data.totals[key]}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#71717A] mt-3">
        Last touch per address: resubmitting the form overwrites the stored
        campaign. Someone who clicks an ad and later signs up with a different
        address counts as direct. Ad spend lives in Google Ads and Meta Ads
        Manager, not here.
      </p>
    </div>
  );
}

export default function OverviewPage() {
  const [stats, setStats] = useState(null);
  const [acquisition, setAcquisition] = useState(null);
  useEffect(() => {
    adminApi.getOverview().then((resp) => setStats(resp.data));
    // Caught on purpose: this table is an addition to a page that already
    // worked, so a failure here must leave the cards and charts standing
    // rather than take the whole Overview down with it.
    adminApi.getAcquisition()
      .then((resp) => setAcquisition(resp.data))
      .catch(() => setAcquisition(null));
  }, []);
  if (!stats) return <p className="text-[#A1A1AA]">Loading...</p>;

  const cards = [
    { label: "Revenue", value: `$${(stats.total_revenue / 100).toFixed(0)}` },
    { label: "Users", value: stats.total_users },
    { label: "Licences", value: stats.total_licences },
    { label: "Orders (this month)", value: stats.monthly_orders },
    { label: "Mac downloads", value: stats.downloads_total?.mac ?? 0 },
    { label: "Windows downloads", value: stats.downloads_total?.windows ?? 0 },
  ];

  const revenue = (stats.series?.revenue ?? []).map((r) => ({
    ...r, dollars: (r.amount ?? 0) / 100, label: fmtDay(r.date),
  }));
  const signups = (stats.series?.signups ?? []).map((r) => ({ ...r, label: fmtDay(r.date) }));
  const downloads = (stats.series?.downloads ?? []).map((r) => ({ ...r, label: fmtDay(r.date) }));
  const activations = (stats.series?.activations ?? []).map((r) => ({ ...r, label: fmtDay(r.date) }));

  return (
    <div>
      <h1 className="font-clash text-2xl font-bold text-white mb-6">Overview</h1>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="feature-card rounded-lg p-4 md:p-6 text-center">
            <p className="text-3xl font-clash font-bold text-white">{c.value}</p>
            <p className="text-xs text-[#A1A1AA] mt-1 font-mono uppercase tracking-wider">{c.label}</p>
          </div>
        ))}
      </div>

      <AcquisitionTable data={acquisition} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Revenue (30 days)">
          <AreaChart data={revenue}>
            <CartesianGrid stroke="#1a1a1e" />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${Number(v).toFixed(0)}`} />
            <Area dataKey="dollars" stroke="#FF5500" fill="#FF5500" fillOpacity={0.25} />
          </AreaChart>
        </ChartCard>

        <ChartCard title="Signups (30 days)">
          <BarChart data={signups}>
            <CartesianGrid stroke="#1a1a1e" />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" fill="#FF5500" />
          </BarChart>
        </ChartCard>

        <ChartCard title="Downloads (30 days)">
          <BarChart data={downloads}>
            <CartesianGrid stroke="#1a1a1e" />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
            <Bar dataKey="mac" stackId="a" fill="#FF5500" />
            <Bar dataKey="windows" stackId="a" fill="#7c7cff" />
          </BarChart>
        </ChartCard>

        <ChartCard title="Licence activations (30 days)">
          <LineChart data={activations}>
            <CartesianGrid stroke="#1a1a1e" />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
            <Line type="monotone" dataKey="paid" stroke="#FF5500" />
            <Line type="monotone" dataKey="trial" stroke="#7c7cff" />
          </LineChart>
        </ChartCard>
      </div>
    </div>
  );
}
